import { useEffect, useState } from 'react'
import { useFormStore, useFormActions, useSignatures, useGeneratedDocuments } from '@hooks/useFormStore'
import { signingApi, clearIdentityDocumentsServerState } from '@services/api'
import { Button } from '@components/UI/Button'
import styles from './Signing.module.css'

export function SigningComplete() {
  const { resetForm, setStep } = useFormActions()
  const signingSessionId = useFormStore((state) => state.signingSessionId)
  const signatures = useSignatures()
  const generatedDocuments = useGeneratedDocuments()
  
  const [isLoading, setIsLoading] = useState(true)
  const [signedDocument, setSignedDocument] = useState<{
    document_id?: string
    completed_at?: string
    signatures: any[]
    completed: boolean
  } | null>(null)

  useEffect(() => {
    loadSignedDocument()
  }, [signingSessionId])

  const loadSignedDocument = async () => {
    if (!signingSessionId) {
      setIsLoading(false)
      return
    }

    try {
      const doc = await signingApi.getSignedDocument(signingSessionId)
      setSignedDocument(doc)
    } catch (err) {
      console.error('Error loading signed document:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = () => {
    // Usa l'endpoint per scaricare il PDF firmato (con Condizioni Tecniche unite)
    if (signingSessionId) {
      const link = document.createElement('a')
      link.href = `/backend/api/v1/download-signed/${signingSessionId}`
      link.download = 'Contratto_Firmato.pdf'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else if (generatedDocuments.length > 0) {
      // Fallback al documento originale (mostra sempre il nome generico al cliente)
      const doc = generatedDocuments[0]
      const link = document.createElement('a')
      link.href = doc.url
      link.download = 'Documento_Contratto.pdf'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleNewRequest = () => {
    resetForm()
    void clearIdentityDocumentsServerState()
    // Step iniziale dopo il reset: 'tipo_cliente' per self-service,
    // 'anagrafica' per la modalita' operatore (lo step 'upload' bolletta
    // e' stato rimosso a maggio 2026).
    setStep('tipo_cliente')
  }

  if (isLoading) {
    return (
      <div className={styles.section}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <p className={styles.loadingText}>Caricamento...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.section}>
      {/* Progress Steps - All Complete */}
      <div className={styles.progressSteps}>
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}>
            <i className="bi bi-check" />
          </div>
          <span className={styles.progressLabel}>Richiedi</span>
        </div>
        <div className={`${styles.progressLine} ${styles.progressStepCompleted}`} />
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}>
            <i className="bi bi-check" />
          </div>
          <span className={styles.progressLabel}>Verifica</span>
        </div>
        <div className={`${styles.progressLine} ${styles.progressStepCompleted}`} />
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}>
            <i className="bi bi-check" />
          </div>
          <span className={styles.progressLabel}>Firma</span>
        </div>
        <div className={`${styles.progressLine} ${styles.progressStepCompleted}`} />
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}>
            <i className="bi bi-check" />
          </div>
          <span className={styles.progressLabel}>Completa</span>
        </div>
      </div>

      {/* Success Card */}
      <div className={styles.successCard}>
        <div className={styles.successIcon}>
          <i className="bi bi-check-circle-fill" />
        </div>
        <h2 className={styles.successTitle}>Documento firmato con successo</h2>
        <p className={styles.successDescription}>
          La procedura di firma è stata completata correttamente.
        </p>

        {/* Summary */}
        <div className={styles.summaryCard}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>
              <i className="bi bi-file-earmark-check" />
              Documento
            </span>
            <span className={styles.summaryValue}>
              Documento Contratto
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>
              <i className="bi bi-calendar-check" />
              Data firma
            </span>
            <span className={styles.summaryValue}>
              {signedDocument?.completed_at
                ? new Date(signedDocument.completed_at).toLocaleString('it-IT')
                : new Date().toLocaleString('it-IT')}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>
              <i className="bi bi-shield-check" />
              Firme applicate
            </span>
            <span className={styles.summaryBadge}>
              {signatures.length || signedDocument?.signatures?.length || 3} firme
            </span>
          </div>
        </div>

        {/* Reference */}
        {signingSessionId && (
          <div className={styles.referenceCard}>
            <p className={styles.referenceLabel}>Riferimento</p>
            <p className={styles.referenceValue}>
              {signingSessionId.slice(0, 8).toUpperCase()}
            </p>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className={styles.infoCard}>
        <div className={styles.infoCardContent}>
          <div className={styles.infoCardIcon}>
            <i className="bi bi-download" />
          </div>
          <div className={styles.infoCardText}>
            <h4>Cosa succede ora?</h4>
            <p>
              Il documento firmato è stato salvato e verrà elaborato. 
              Riceverai una copia via email all'indirizzo associato alla tua richiesta.
            </p>
          </div>
        </div>
      </div>

      {/* Download Button */}
      {(signingSessionId || generatedDocuments.length > 0) && (
        <div className={styles.downloadSection}>
          <button
            type="button"
            className={styles.downloadButton}
            onClick={handleDownload}
          >
            <i className="bi bi-download" />
            Scarica documento firmato
          </button>
        </div>
      )}

      {/* New Request Button */}
      <Button
        type="button"
        variant="secondary"
        icon="plus-lg"
        fullWidth
        onClick={handleNewRequest}
      >
        Nuova Richiesta
      </Button>

      {/* Security Footer */}
      <div className={styles.securityFooter}>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-neutral-400)' }}>
          Grazie per aver utilizzato il nostro servizio di firma digitale
        </p>
      </div>
    </div>
  )
}

